import React, { useState, useRef, useEffect } from 'react';
import { API_ENDPOINTS } from '../config/api';
import './AnswerAudioPlayer.css';

const AnswerAudioPlayer = ({ answer, query, onAudioGenerated }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);

  // Generate audio when component mounts
  useEffect(() => {
    if (answer) {
      generateAudio();
    }
  }, [answer]);

  const generateAudio = async () => {
    if (!answer) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(API_ENDPOINTS.AUDIO_GENERATE_ANSWER, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          answer: answer,
          query: query || ''
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Construct the full audio URL using the answerId
        const audioFileUrl = API_ENDPOINTS.AUDIO_FILE(data.answerId);
        setAudioUrl(audioFileUrl);
        if (onAudioGenerated) {
          onAudioGenerated({ ...data, audioUrl: audioFileUrl });
        }
      } else {
        setError(data.error || 'Failed to generate audio');
      }
    } catch (err) {
      console.error('Answer audio generation error:', err);
      setError('Failed to generate audio');
    } finally {
      setIsLoading(false);
    }
  };

  const togglePlayPause = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  const handleAudioError = () => {
    setError('Failed to play audio');
    setIsPlaying(false);
  };

  if (error) {
    return (
      <div className="answer-audio-player error">
        <button 
          className="answer-audio-btn error-btn"
          onClick={generateAudio}
          title="Retry audio generation"
        >
          <div className="answer-audio-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </div>
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="answer-audio-player loading">
        <div className="answer-audio-btn loading-btn">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (!audioUrl) {
    return (
      <div className="answer-audio-player">
        <button 
          className="answer-audio-btn generate-btn"
          onClick={generateAudio}
          title="Generate audio for complete answer"
        >
          <div className="answer-audio-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z"/>
            </svg>
          </div>
        </button>
      </div>
    );
  }

  return (
    <div className="answer-audio-player">
      <button 
        className={`answer-audio-btn ${isPlaying ? 'playing' : 'play'}`}
        onClick={togglePlayPause}
        title={isPlaying ? 'Pause answer audio' : 'Play complete answer audio'}
      >
        <div className="answer-audio-icon">
          {isPlaying ? (
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z"/>
            </svg>
          )}
        </div>
      </button>
      <audio
        ref={audioRef}
        src={audioUrl}
        onEnded={handleAudioEnded}
        onError={handleAudioError}
        preload="none"
      />
    </div>
  );
};

export default AnswerAudioPlayer;
